import React, { useCallback, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { INicheMap, IGif } from "services/types";
import { IUserList } from "./GifList";
import { Player } from "components/player/Player";
import { useFullScreen } from "hooks/useFullScreen";
import { useFSNav } from "hooks/useFSNav";
import { ViewRegion } from "utils/metrics";
import { PlayerPlaceholder } from "components/UI/Skeleton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { TABLET_WIDTH } from "hooks/useDeviceType";

import "./PreviewFeed.scss";

interface IProps {
  gifs: IGif[];
  users: IUserList;
  niches?: INicheMap;
  hasMore: boolean;
  fetchMore: () => void;
  injector?: (pos: number, gif: IGif, fullScreen: boolean) => JSX.Element;
  rel?: string;
  source: ViewRegion;
  isLoading?: boolean;
}

const PLACEHOLDER_ITEMS_LENGTH = 6;

export const PreviewFeed = (props: IProps) => {
  // Track visible gifs, the first of which should autoplay.
  const [visible, setVisible] = useState<string[]>([]);
  // Sometimes we enable fullscreen in the middle of the feed,
  // so we need to manually scroll to the correct slide, and
  // this sometimes makes IntersectionObserver not to trigger.
  // So let's additionally track the active slide to force render.
  const [activeGif, setActiveGif] = useState<string|undefined>(undefined);
  const [hiddenGifs, setHiddenGifs] = useState<string[]>([]);
  const filteredGifs = props.gifs.filter((gif) => !hiddenGifs.includes(gif.id));
  const feed = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery(`(min-width: ${TABLET_WIDTH}px)`);
  const {
    inFullscreenMode,
    toggleFullscreen,
    FSGif
  } = useFullScreen({ ref: feed });

  useFSNav({
    isFS: inFullscreenMode,
    setActiveGif,
    gifs: props.gifs,
    fetchMore: props.fetchMore,
    hasMore: props.hasMore,
    ref: feed,
    toggleFullscreen,
    FSGif
  });

  const onVisible = useCallback((gif: string, visible: boolean): void => {
    if (visible) {
      setVisible((prev: string[]) => [...prev, gif]);

      // The activeGif variable is maintained manually as a back up for
      // when the visibility observer not fires on quick programmatic scroll.
      // When it does fire, we don't need that value anymore, so reset it.
      setActiveGif(undefined);
    } else {
      setVisible((prev: string[]) => prev.filter((x) => x !== gif));
    }
  }, []);

  const isActive = (id: string): boolean => {
    const active = inFullscreenMode ?
      activeGif ?? visible[0] : visible[0] ?? activeGif;

    return active === id;
  };

  const hideGif = (id: string) => {
    setHiddenGifs((prev: any) => [...prev, id]);
  };

  const renderGifs = () => {
    if (!filteredGifs.length && props.isLoading) {
      return new Array(PLACEHOLDER_ITEMS_LENGTH).fill("").map((___, index) => (
        <React.Fragment key={index}>
          <PlayerPlaceholder />
          {/* @ts-ignore */}
          {props.injector && props.injector(index, { id: "load" }, inFullscreenMode)}
        </React.Fragment>
      ));
    }

    if (!filteredGifs.length && !props.isLoading) {
      return <p>No gifs to show on this page.</p>;
    }

    return filteredGifs.map((gif: IGif, index: number) => (
      <React.Fragment key={index}>
        <Player
          key={gif.id}
          gif={gif}
          user={props.users[gif.userName]}
          niches={props.niches}
          click={toggleFullscreen}
          fullscreen={inFullscreenMode}
          active={isActive(gif.id)}
          toggleFullScreen={toggleFullscreen}
          onVisible={onVisible}
          rel={props.rel}
          position={index + 1}
          source={props.source}
          hideGif={hideGif}
        />

        {props.injector && props.injector(index, gif, inFullscreenMode)}
      </React.Fragment>
    ));
  };

  return (
    <InfiniteScroll
      dataLength={props.gifs.length}
      hasMore={props.hasMore}
      next={props.fetchMore}
      loader={<></>}
      scrollableTarget={!isDesktop ? "scrollableDiv" : undefined}
    >
      <div className="previewFeed" ref={feed}>
        {renderGifs()}
      </div>
    </InfiniteScroll>
  );
};
